/**
 * Umi 运行时配置：初始用户、ProLayout、全局请求错误处理、后台菜单权限门禁。
 *
 * 公开落地页（/、/landing/*）与登录页不预取用户信息；
 * 其余路由依赖 {@link @/constants/adminAuth} 与后端返回的 menuCodes/actionCodes。
 */
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  SelectLang,
} from '@/components';
import {
  findFirstAccessiblePath,
  getRouteMenuCode,
  hasAdminConsoleAccess,
  hasMenuPermission,
} from '@/constants/adminAuth';
import { reportMonitoringEvent, setupWebMonitoring } from '@/monitoring';
import { queryCurrentUser } from '@/services/ant-design-pro/api';
import {
  clearUserTokens,
  getUserAccessToken,
  redirectToUserLogin,
} from '@/services/auth';
import defaultSettings from '@config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';
const isPublicPath = (pathname: string) =>
  pathname === '/' || pathname === loginPath || pathname.startsWith('/landing');

setupWebMonitoring();

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    if (!getUserAccessToken()) {
      return undefined;
    }
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      clearUserTokens();
      redirectToUserLogin(`${history.location.pathname}${history.location.search}`);
    }
    return undefined;
  };
  // 公开官网与登录页不需要预取后台用户信息
  const { location } = history;
  if (!isPublicPath(location.pathname)) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const redirectToAccessiblePage = () => {
    const nextPath = findFirstAccessiblePath(initialState?.currentUser);
    history.replace(nextPath || '/403');
  };

  const filterMenuItems = (items: any[] = []): any[] =>
    items
      .map((item) => {
        const children = filterMenuItems(item.children || item.routes || []);
        const allowed = hasMenuPermission(initialState?.currentUser, item.menuCode);
        if (!allowed && children.length === 0) {
          return null;
        }
        return {
          ...item,
          children,
        };
      })
      .filter(Boolean);

  return {
    actionsRender: () => [
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren as any}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      void reportMonitoringEvent({
        eventType: 'page_view',
        eventName: 'page_change',
        level: 'info',
        page: location.pathname,
        route: `${location.pathname}${location.search}`,
      });
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && !isPublicPath(location.pathname)) {
        redirectToUserLogin(`${location.pathname}${location.search}`);
        return;
      }
      if (
        initialState?.currentUser &&
        !hasAdminConsoleAccess(initialState.currentUser) &&
        !isPublicPath(location.pathname)
      ) {
        clearUserTokens();
        redirectToUserLogin(`${location.pathname}${location.search}`);
        return;
      }
      if (
        initialState?.currentUser &&
        !isPublicPath(location.pathname) &&
        location.pathname !== '/403'
      ) {
        const menuCode = getRouteMenuCode(location.pathname);
        if (menuCode && !hasMenuPermission(initialState.currentUser, menuCode)) {
          redirectToAccessiblePage();
        }
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    menuHeaderRender: undefined,
    menuDataRender: (menuData) => filterMenuItems(menuData as any[]),
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: '/',
  ...errorConfig,
};
